import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** Session flag — cleared on successful app boot in main.tsx. */
export const CHUNK_RELOAD_SESSION_KEY = 'larkpm:chunk-reload';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('is not a valid javascript mime type') ||
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Drop-in replacement for React.lazy that recovers from stale cached index.html
 * after a deploy. When CloudFront serves index.html for a missing hashed chunk,
 * the browser throws a MIME-type error — one hard reload fetches fresh HTML.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) !== '1') {
        sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {
          /* page reload in progress */
        });
      }
      sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
      throw error;
    }
  });
}
